import { html, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafFlexContainer } from '@cigna/dcws_leaf-web-components/components/flex-container/flex-container.component.js';
import { LeafList } from '@cigna/dcws_leaf-web-components/components/list/list.component.js';
import { LeafModal } from '@cigna/dcws_leaf-web-components/components/modal/modal.component.js';
import { LeafProgressBar } from '@cigna/dcws_leaf-web-components/components/progress-bar/progress-bar.component.js';
import { LeafRadio } from '@cigna/dcws_leaf-web-components/components/radio/radio.component.js';
import { LeafRadiogroup } from '@cigna/dcws_leaf-web-components/components/radiogroup/radiogroup.component.js';
import { CHCHeadingList } from '@cigna/dcws_leaf-web-components/chc-components/heading-list/heading-list.component.js';
import { CHCHeadingListItem } from '@cigna/dcws_leaf-web-components/chc-components/heading-list-item/heading-list-item.component.js';
import { CHCPhoneNumber } from '@cigna/dcws_leaf-web-components/chc-components/phone-number/phone-number.component.js';
import { CHCMedicarePlanFinder } from '../medicare-plan-finder/medicare-plan-finder.component.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { resultsSection } from './results-section.js';
import styles from './medicare-shopping-wizard.scss?inline';
import type { ShoppingWizardData, Step } from './interfaces.js';

export class CHCMedicareShoppingWizard extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-flex-container': LeafFlexContainer,
    'leaf-list': LeafList,
    'leaf-modal': LeafModal,
    'leaf-progress-bar': LeafProgressBar,
    'leaf-radio': LeafRadio,
    'leaf-radiogroup': LeafRadiogroup,
    'chc-heading-list': CHCHeadingList,
    'chc-heading-list-item': CHCHeadingListItem,
    'chc-phone-number': CHCPhoneNumber,
    'chcapp-medicare-plan-finder': CHCMedicarePlanFinder,
  };
  protected override ns = 'medicare-shopping-wizard';
  private shoppingWizardData?: ShoppingWizardData;

  @property({ type: String, reflect: true })
  variant!: string;

  @property()
  planType: 'Supp' | 'MAPD' | 'PDP' = 'Supp';

  @state()
  currentStep = 0;

  @state()
  score: { [key: string]: number } = { B: 0, A: 0 };

  @state()
  heading = html``;

  @state()
  possibleValues: { [key: string]: number } = { B: 0, A: 0 };

  @state()
  nextOrFinish: string | undefined;

  @query('#wizard-back')
  _backBtn!: HTMLElement;

  override onNamespacesLoaded = () => {
    this.shoppingWizardData = this.i18n?.getLanguageData(this.ns) as ShoppingWizardData;
    this.nextOrFinish = this.shoppingWizardData?.misc.nextBtn;
    this.heading = this.renderHeading();
    this.getPossibleValues();
  };

  public startOver() {
    this.showResults();
    this.currentStep = 0;
    this.score = { B: 0, A: 0 };
    this.nextOrFinish = this.shoppingWizardData?.misc.nextBtn;
    this.heading = this.renderHeading();
    const groups = this.shadowRoot?.querySelectorAll('chc-heading-list-item');
    if (groups) {
      [groups[groups.length - 1], groups[0], this._backBtn]?.forEach((el) => {
        el?.toggleAttribute('hidden');
      });
    }
    this.getPossibleValues();
  }

  //TODO: Why is this a slot?
  renderStepContent = (step: { content: string }) => {
    if (step.content) {
      return html`<slot name="step-content"><div slot="step-content">${step.content}</div></slot>`;
    } else {
      return;
    }
  };

  renderWizardOptions(step: Step) {
    if (step.options?.length > 0) {
      let index = 0;
      if (this.shoppingWizardData?.steps) {
        index = this.shoppingWizardData?.steps?.indexOf(step);
      }
      return html`
        <form>
          <leaf-radiogroup required name="step-${index + 1}" id="step-id-${index + 1}" value=${step.options[0].value}>
            ${step.options?.map(
              (option) => html`<leaf-radio value="${option.value}">${unsafeHTML(option.label)}</leaf-radio>`,
            )}
          </leaf-radiogroup>
        </form>
      `;
    }
    return html``;
  }

  renderFootNote(step: Step) {
    if (step.footNote) {
      return html`<slot name="foot-note"><div slot="foot-note">${step.footNote}</div></slot>`;
    } else {
      return;
    }
  }

  renderSteps() {
    return html`
      <chc-heading-list color="green">
        ${this.shoppingWizardData?.steps?.map((step) => {
          return html`<chc-heading-list-item ?hidden=${this.shoppingWizardData?.steps?.indexOf(step) !== 0} slot="item">
            ${this.renderStepContent(step)} ${this.renderWizardOptions(step)} ${this.renderFootNote(step)}
          </chc-heading-list-item>`;
        })}
      </chc-heading-list>
    `;
  }

  updateScore() {
    this.shadowRoot?.querySelectorAll('leaf-radiogroup').forEach((item) => {
      const option = item.getAttribute('value');
      if (option) {
        this.possibleValues[`${option}`] += 1;
      }
    });
    this.score = this.possibleValues;
  }

  getPossibleValues() {
    const values: { [x: string]: number } = {};
    this.shoppingWizardData?.steps?.forEach((step) => {
      step.options.forEach((option: { value: string }) => {
        if (!(option.value in values)) {
          values[option.value] = 0;
        }
      });
    });
    this.possibleValues = values;
  }

  renderHeading(result: string | null = null) {
    if (result) {
      return html`${result}`;
    } else {
      return html`
        ${this.shoppingWizardData?.steps[this.currentStep].header} (${this.currentStep + 1} of
        ${this.shoppingWizardData?.steps?.length})
      `;
    }
  }

  showResults() {
    const getWinner = Object.keys(this.score).reduce((a, b) => (this.score[a] > this.score[b] ? a : b));
    const getWinnerObj = this.shoppingWizardData?.results.filter((obj: { result: string }) => obj.result === getWinner);
    [
      'chc-heading-list',
      '#wizard-next',
      '#wizard-back',
      '#wizard-start-over',
      'chcapp-medicare-plan-finder',
      'leaf-progress-bar',
    ]?.forEach((el) => this.shadowRoot?.querySelector(el)?.toggleAttribute('hidden'));
    this.shadowRoot?.querySelectorAll(`div[data-result="${getWinner}"]`).forEach((el) => el?.toggleAttribute('hidden'));
    this.planType = getWinner === 'A' ? 'Supp' : 'MAPD';
    if (getWinnerObj && getWinnerObj[0].heading) {
      this.heading = this.renderHeading(getWinnerObj[0].heading);
    }
  }

  toggleStepView(direction = 'next') {
    const groups = this.shadowRoot?.querySelectorAll('chc-heading-list-item') || [];
    const currentGroup = [...groups].filter((group) => !group.hasAttribute('hidden'));
    const nextIndex =
      direction === 'next' ? [...groups].indexOf(currentGroup[0]) + 1 : [...groups].indexOf(currentGroup[0]) - 1;
    const isComplete = nextIndex + 1 >= groups.length;

    this.nextOrFinish = isComplete ? this.shoppingWizardData?.misc.finish : this.shoppingWizardData?.misc.nextBtn;
    if (nextIndex === groups.length) {
      this.updateScore();
      this.showResults();
      this.currentStep += 1;
    } else {
      currentGroup[0].toggleAttribute('hidden');
      groups[nextIndex].toggleAttribute('hidden');
      this.currentStep = nextIndex;
      if (nextIndex === 0) {
        this._backBtn?.setAttribute('hidden', '');
      }
      if (direction === 'next') this._backBtn?.removeAttribute('hidden');
      if (nextIndex === 0) this._backBtn?.setAttribute('hidden', '');
      this.heading = this.renderHeading();
    }
  }

  next() {
    this.toggleStepView();
  }
  back() {
    this.toggleStepView('back');
  }
  override render() {
    const loadingOrError = this.renderLoadingOrError();
    if (loadingOrError) {
      return loadingOrError;
    }

    return html` <div class="chcapp-shopping-wizard">
      <leaf-modal id="shopping-wizard-modal" size="md">
        <h2 slot="heading">${this.heading}</h2>
        <leaf-progress-bar
          variant="plain"
          value="${this.currentStep + 1}"
          max="${ifDefined(this.shoppingWizardData?.steps?.length)}"
          ?showFullBar=${true}
          precision="1"
        ></leaf-progress-bar>
        <div>${this.renderSteps()} ${resultsSection(this.shoppingWizardData, this.planType)}</div>
        <leaf-button
          variant="secondary"
          id="wizard-start-over"
          class="chcapp-shopping-wizard--start-over-button"
          hidden
          @click="${this.startOver}"
          >${this.shoppingWizardData?.misc.startOver}</leaf-button
        >
        <leaf-flex-container slot="cta">
          <leaf-button variant="primary" id="wizard-next" @click="${this.next}">${this.nextOrFinish}</leaf-button>
          <leaf-button variant="secondary" id="wizard-back" hidden @click="${this.back}"
            >${this.shoppingWizardData?.misc.backBtn}</leaf-button
          >
        </leaf-flex-container>
      </leaf-modal>
    </div>`;
  }
}
